import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import mockProjects from '../data/mockProjects.json';
import { projectsAPI, agComponentsAPI, jobsAPI} from '../services/api';
import { updateAgComponentsFields } from '../utils/agComponentFieldMerger';
import useCanvasStore from '../stores/canvasStore';

// This hook ONLY loads project data. Canvas operations should be handled by the component that owns the canvas.
export const useOpenAgProject = (canvasOperations = {}) => {
  const { projectId } = useParams();
  const {
    setProjectName,
    setCanvasSize,
    setCanvasBackgroundColor,
    viewport,
    pixiInitialized,
    clearHistory,
    updateElement,
    addComponentToCanvas
  } = canvasOperations;
  
  // Get the setIsProjectLoading function from the store
  const setIsProjectLoading = useCanvasStore((state) => state.setIsProjectLoading);
  const setExpectedLayerCount = useCanvasStore((state) => state.setExpectedLayerCount);
  const setProjectLoadFailed = useCanvasStore((state) => state.setProjectLoadFailed);
  
  const [isLoading, setIsLoading] = useState(false);
  const [projectData, setProjectData] = useState(null);
  const [initializationError, setInitializationError] = useState(null);
  const hasInitializedProject = useRef(false);
  
  // Loading progress state
  const [loadingProgress, setLoadingProgress] = useState({
    isOpen: false,
    progress: 0,
    currentAsset: '',
    totalAssets: 0,
    loadedAssets: 0,
    stage: 'initializing'
  });

  // Load project data when projectId changes
  useEffect(() => {
    const loadProject = async () => {
      if (projectId) {
        setIsLoading(true);
        setInitializationError(null);
        
        try {
          // Fetch project from API
          const response = await projectsAPI.getProject(projectId);
          const project = response.data.project;
          
          if (project) {
            setProjectData(project);
            // Update project name in the store
            if (setProjectName && project.name) {
              setProjectName(project.name);
            }
            console.log("Project data loaded:", project.name);
          } else {
            setInitializationError(`Project with ID ${projectId} not found`);
            // console.error(`Project not found: ${projectId}`);
          }
        } catch (error) {
          console.error("Error fetching project:", error);
          setInitializationError(`Failed to load project: ${error.message}`);
        } finally {
          setIsLoading(false);
        }
      } else {
        // Reset when no projectId (new blank project)
        setProjectData(null);
        hasInitializedProject.current = false;
      }
    };

    loadProject();
  }, [projectId]);

  // Initialize project when PIXI is ready and project data is loaded
  useEffect(() => {
    const initializeProject = async () => {
      if (!projectData || !pixiInitialized || !viewport || hasInitializedProject.current) {
        return;
      }

      console.log("🚀 Initializing project:", projectData.name);
      hasInitializedProject.current = true;
      setInitializationError(null);
      
      // Set loading state to prevent autosave during project load
      setIsProjectLoading(true);
      setProjectLoadFailed(false);
      
      // Track expected layer count BEFORE loading begins
      // This prevents autosave from overwriting layers if loading fails
      const totalLayers = projectData.layers?.length || 0;
      setExpectedLayerCount(totalLayers);
      setLoadingProgress({
        isOpen: true,
        progress: 0,
        currentAsset: 'Initializing canvas...',
        totalAssets: totalLayers,
        loadedAssets: 0,
        stage: 'initializing'
      });

      try {
        // Clear history for fresh project load
        clearHistory();

        // Set canvas size from project (including name)
        if (projectData.canvas) {
          setCanvasSize({
            width: projectData.canvas.width || 1080,
            height: projectData.canvas.height || 1080,
            name: projectData.canvas.name || `${projectData.canvas.width || 1080} × ${projectData.canvas.height || 1080}`
          });
          console.log(`📐 Canvas size set to: ${projectData.canvas.width}x${projectData.canvas.height}`);

          if (setCanvasBackgroundColor && projectData.canvas.backgroundColor) {
            setCanvasBackgroundColor(projectData.canvas.backgroundColor, true);
          }
        }

        // Update progress to loading assets stage
        setLoadingProgress(prev => ({
          ...prev,
          progress: 10,
          currentAsset: 'Preparing assets...',
          stage: 'loading-assets'
        }));

        // Load layers/elements
        if (projectData.layers && projectData.layers.length > 0) {
          console.log(`📦 Loading ${projectData.layers.length} layers...`);
          
          let allLayers = [...projectData.layers];

          // Update AG components with latest fields from backend
          console.log('🔄 Checking for AG component field updates...');
          
          // Function to fetch latest component data from jobs
          // const fetchLatestComponent = async (componentId) => {
          //   try {
          //     // Fetch from agComponents API which gets data from the Job model
          //     const response = await agComponentsAPI.getById(componentId);
              
          //     if (response.data && response.data.data) {
          //       const component = response.data.data;
          //       // console.log(`Fetched latest data for component ${componentId} from jobs`);
          //       return component;
          //     }
              
          //     // console.log(`Component ${componentId} not found in jobs`);
          //     return null;
          //   } catch (error) {
          //     console.error(`Error fetching component ${componentId} from jobs:`, error);
          //     return null;
          //   }
          // };

          // Update all AG components with latest fields
          // const updatedLayers = await updateAgComponentsFields(allLayers, fetchLatestComponent, jobsAPI.patchJobUpdates);
          
          // // Check if any updates were made
          // const hasUpdates = updatedLayers.some((layer, index) => {
          //   return JSON.stringify(layer.componentData?.fields) !== JSON.stringify(allLayers[index].componentData?.fields);
          // });
          
          // if (hasUpdates) {
          //   console.log(' AG component fields updated with latest definitions');
          //   allLayers = updatedLayers;
            
          //   // Save the updated project data back to the database
          //   try {
          //     const updatedProjectData = {
          //       ...projectData,
          //       layers: updatedLayers
          //     };
              
          //     // await projectsAPI.saveProject(updatedProjectData);
          //     console.log(' Updated project saved with new field definitions');
          //   } catch (saveError) {
          //     console.error('Error saving updated project:', saveError);
          //     // Continue with loading even if save fails
          //   }
          // } else {
          //   console.log(' All AG components are up to date');
          // }

          // Add each layer to canvas and update it on the backend
          let loadedCount = 0;
          let failedLayers = [];
          const baseProgress = 10;
          const progressPerLayer = (90 - baseProgress) / allLayers.length;
          
          // Timeout configurations for production network scenarios
          const BACKEND_UPDATE_TIMEOUT = 30000; // 30 seconds for backend updates
          const LAYER_LOAD_TIMEOUT = 60000; // 60 seconds per layer (for large video files)
          
          for (let i = 0; i < allLayers.length; i++) {
            const layer = allLayers[i];
            
            // Update progress before loading each layer
            const layerName = layer.name || layer.componentData?.name || `Layer ${i + 1}`;
            setLoadingProgress(prev => ({
              ...prev,
              currentAsset: layerName,
              loadedAssets: loadedCount,
              progress: baseProgress + (loadedCount * progressPerLayer)
            }));
            
            try {
              // Update the job in the backend with timeout protection
              // if(layer.componentData.fields && hasUpdates){
              //   try {
              //     await Promise.race([
              //       jobsAPI.patchJobUpdates(layer.id, layer.componentData.id),
              //       new Promise((_, reject) =>
              //         setTimeout(() => reject(new Error('Backend update timeout')), BACKEND_UPDATE_TIMEOUT)
              //       )
              //     ]);
              //   } catch (backendError) {
              //     console.warn(`⚠️ Backend update failed for ${layerName}, continuing with layer load:`, backendError.message);
              //     // Don't fail the entire layer load if backend update fails
              //   }
              // }
              
              // Calculate position from transform data
              const position = {
                x: layer.x || 960,
                y: layer.y || 540
              };
              
              const{...layerProperties} = layer

              // Add component to canvas (creates the PIXI sprite) with timeout protection
              await Promise.race([
                addComponentToCanvas(layer.componentData, position, layerProperties),
                new Promise((_, reject) =>
                  setTimeout(() => reject(new Error('Layer load timeout - asset may be too large or network is slow')), LAYER_LOAD_TIMEOUT)
                )
              ]);

              console.log(`✅ Added layer: ${layer.name}`);
              
              // Update loaded count and progress after successful load
              loadedCount++;
              setLoadingProgress(prev => ({
                ...prev,
                loadedAssets: loadedCount,
                progress: baseProgress + (loadedCount * progressPerLayer)
              }));
              
            } catch (error) {
              console.error(`❌ Error adding layer ${layerName}:`, error);
              failedLayers.push({
                name: layerName,
                error: error.message,
                index: i + 1
              });
              
              // Update loaded count even on failure to keep progress moving
              loadedCount++;
              setLoadingProgress(prev => ({
                ...prev,
                loadedAssets: loadedCount,
                progress: baseProgress + (loadedCount * progressPerLayer)
              }));
            }
          }
          
          // Report failed layers if any (but don't show error to user - placeholders will be created)
          if (failedLayers.length > 0) {
            console.warn(`⚠️ ${failedLayers.length} of ${allLayers.length} layer(s) failed to load (placeholders created):`, failedLayers);
            // Don't set initialization error - placeholders will handle the failed layers
            // BUT mark the project load as failed to prevent autosave from overwriting layers
            setProjectLoadFailed(true);
            console.warn('🛡️ Project load had failures - autosave will be blocked to prevent layer data loss');
          }

          // Finalize loading
          setLoadingProgress(prev => ({
            ...prev,
            progress: 95,
            currentAsset: 'Finalizing...',
            stage: 'finalizing'
          }));

          clearHistory();
          console.log("🎉 Project loaded successfully!");
          
          // Complete loading
          setTimeout(() => {
            setLoadingProgress(prev => ({
              ...prev,
              progress: 100,
              currentAsset: 'Complete!',
              isOpen: false
            }));
            // Clear loading state to allow autosave
            setIsProjectLoading(false);
          }, 500);
        } else {
          console.log("📭 No layers to load - starting with empty canvas");
          // Close loading progress for empty projects
          setTimeout(() => {
            setLoadingProgress(prev => ({
              ...prev,
              progress: 100,
              currentAsset: 'Complete!',
              isOpen: false
            }));
            // Clear loading state to allow autosave
            setIsProjectLoading(false);
          }, 500);
        }
      } catch (error) {
        console.error("❌ Error initializing project:", error);
        setInitializationError(error.message);
        // Close loading progress on error
        setLoadingProgress(prev => ({
          ...prev,
          isOpen: false
        }));
        // Mark project load as failed to prevent autosave from overwriting layers
        setProjectLoadFailed(true);
        console.warn('🛡️ Project initialization failed - autosave blocked to prevent layer data loss');
        // Clear loading state even on error
        setIsProjectLoading(false);
      }
    };

    initializeProject();
  }, [
    projectData, 
    pixiInitialized, 
    viewport, 
    setCanvasSize, 
    setCanvasBackgroundColor,
    addComponentToCanvas,
    updateElement,
    clearHistory,
    setIsProjectLoading,
    setExpectedLayerCount,
    setProjectLoadFailed
  ]);

  // Reset initialization flag when projectId changes
  useEffect(() => {
    hasInitializedProject.current = false;
  }, [projectId]);

  return {
    projectData,
    isLoading,
    initializationError,
    isProjectLoaded: hasInitializedProject.current,
    loadingProgress
  };
};
